
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type BlogPost = Partial<Database['public']['Tables']['blog_posts']['Row']> & {
  author?: {
    first_name?: string;
    last_name?: string;
  } | null;
  tags?: { tag_id: { id: string; name: string } }[];
  itineraries?: {
    itineraries?: {
      id: string;
      title: string;
      destination?: string | null;
      slug?: string | null;
    };
  }[];
};

export async function fetchBlogPost(slug: string): Promise<BlogPost> {
  const { data: postData, error: postError } = await supabase
    .from('blog_posts')
    .select(`
      *,
      tags:blog_posts_tags(tag_id(id, name)),
      itineraries:blog_itinerary_links(itineraries(id, title, destination, slug))
    `)
    .eq('status', 'published')
    .eq('slug', slug)
    .maybeSingle();

  if (postError) throw postError;
  if (!postData) throw new Error('Blog post not found');

  // Create properly typed result
  const result: BlogPost = { ...postData };
  
  // Fetch author data separately if author_id exists
  if (postData.author_id) {
    const { data: authorData } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', postData.author_id)
      .maybeSingle();
    
    if (authorData) {
      result.author = authorData;
    }
  }

  return result;
}

export async function fetchBlogPosts(
  page: number,
  limit: number,
  tag?: string,
  category?: string
) {
  console.log('[DEBUG] fetchBlogPosts called with:', { page, limit, tag, category });

  // If filtering by specific tag, get posts with that exact tag
  if (tag) {
    console.log('[DEBUG] Filtering by specific tag:', tag);
    
    const { data: taggedPostIds, error: tagError } = await supabase
      .from('blog_posts_tags')
      .select('blog_post_id, tag_id!inner(id, name)')
      .eq('tag_id.name', tag);

    if (tagError) {
      console.error('[DEBUG] Tag filter error:', tagError);
      throw tagError;
    }

    console.log('[DEBUG] Tagged post IDs found:', taggedPostIds);

    if (!taggedPostIds || taggedPostIds.length === 0) {
      console.log('[DEBUG] No posts found for tag:', tag);
      return { data: [], count: 0 };
    }

    const postIds = taggedPostIds.map(item => item.blog_post_id);
    console.log('[DEBUG] Post IDs to fetch:', postIds);

    const { data, count, error } = await supabase
      .from('blog_posts')
      .select(`
        *,
        tags:blog_posts_tags(tag_id(id, name, category))
      `, { count: 'exact' })
      .eq('status', 'published')
      .in('id', postIds)
      .order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    if (error) {
      console.error('[DEBUG] Error fetching tagged posts:', error);
      throw error;
    }

    // Enrich with flags to avoid N+1 card queries
    const ids = (data || []).map(p => p.id);
    const [packingLinks, itineraryLinks] = await Promise.all([
      ids.length
        ? supabase.from('blog_packing_list_links').select('blog_post_id').in('blog_post_id', ids)
        : Promise.resolve({ data: [], error: null }),
      ids.length
        ? supabase.from('blog_itinerary_links').select('blog_post_id').in('blog_post_id', ids)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const packingSet = new Set((packingLinks.data || []).map((r: any) => r.blog_post_id));
    const itinerarySet = new Set((itineraryLinks.data || []).map((r: any) => r.blog_post_id));

    const enhancedData = (data || []).map(p => ({
      ...p,
      hasPackingList: packingSet.has(p.id),
      hasItineraries: itinerarySet.has(p.id),
    }));

    console.log('[DEBUG] Tagged posts result:', { count, dataLength: enhancedData?.length });
    return { data: enhancedData || [], count: count || 0 };
  }

  // If filtering by category, get all tags in that category first, then get posts with those tags
  if (category) {
    console.log('[DEBUG] Filtering by category:', category);
    
    // First, get all tags that belong to this category
    const { data: categoryTags, error: categoryTagsError } = await supabase
      .from('tags')
      .select('id, name')
      .eq('category', category);

    if (categoryTagsError) {
      console.error('[DEBUG] Category tags error:', categoryTagsError);
      throw categoryTagsError;
    }

    console.log('[DEBUG] Tags in category:', categoryTags);

    if (!categoryTags || categoryTags.length === 0) {
      console.log('[DEBUG] No tags found for category:', category);
      return { data: [], count: 0 };
    }

    // Get all blog post IDs that have any of these tags
    const tagIds = categoryTags.map(tag => tag.id);
    const { data: categoryPostIds, error: categoryPostError } = await supabase
      .from('blog_posts_tags')
      .select('blog_post_id')
      .in('tag_id', tagIds);

    if (categoryPostError) {
      console.error('[DEBUG] Category posts error:', categoryPostError);
      throw categoryPostError;
    }

    console.log('[DEBUG] Post IDs for category:', categoryPostIds);

    if (!categoryPostIds || categoryPostIds.length === 0) {
      console.log('[DEBUG] No posts found for category:', category);
      return { data: [], count: 0 };
    }

    // Get unique post IDs
    const uniquePostIds = [...new Set(categoryPostIds.map(item => item.blog_post_id))];
    console.log('[DEBUG] Unique post IDs for category:', uniquePostIds);

    const { data, count, error } = await supabase
      .from('blog_posts')
      .select(`
        *,
        tags:blog_posts_tags(tag_id(id, name, category))
      `, { count: 'exact' })
      .eq('status', 'published')
      .in('id', uniquePostIds)
      .order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);

    if (error) {
      console.error('[DEBUG] Error fetching category posts:', error);
      throw error;
    }

    // Enrich with flags to avoid N+1 card queries
    const ids = (data || []).map(p => p.id);
    const [packingLinks, itineraryLinks] = await Promise.all([
      ids.length
        ? supabase.from('blog_packing_list_links').select('blog_post_id').in('blog_post_id', ids)
        : Promise.resolve({ data: [], error: null }),
      ids.length
        ? supabase.from('blog_itinerary_links').select('blog_post_id').in('blog_post_id', ids)
        : Promise.resolve({ data: [], error: null }),
    ]);

    const packingSet = new Set((packingLinks.data || []).map((r: any) => r.blog_post_id));
    const itinerarySet = new Set((itineraryLinks.data || []).map((r: any) => r.blog_post_id));

    const enhancedData = (data || []).map(p => ({
      ...p,
      hasPackingList: packingSet.has(p.id),
      hasItineraries: itinerarySet.has(p.id),
    }));

    console.log('[DEBUG] Category posts result:', { count, dataLength: enhancedData?.length });
    return { data: enhancedData || [], count: count || 0 };
  }

  // Default query for all posts (no tag or category filter)
  console.log('[DEBUG] Fetching all posts (no filters)');
  
  const { data, count, error } = await supabase
    .from('blog_posts')
    .select(`
      *,
      tags:blog_posts_tags(tag_id(id, name, category))
    `, { count: 'exact' })
    .eq('status', 'published')
    .order('created_at', { ascending: false })
    .range((page - 1) * limit, page * limit - 1);

  if (error) {
    console.error('[DEBUG] Error fetching all posts:', error);
    throw error;
  }

  // Enrich with flags to avoid N+1 card queries
  const ids = (data || []).map(p => p.id);
  const [packingLinks, itineraryLinks] = await Promise.all([
    ids.length
      ? supabase.from('blog_packing_list_links').select('blog_post_id').in('blog_post_id', ids)
      : Promise.resolve({ data: [], error: null }),
    ids.length
      ? supabase.from('blog_itinerary_links').select('blog_post_id').in('blog_post_id', ids)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const packingSet = new Set((packingLinks.data || []).map((r: any) => r.blog_post_id));
  const itinerarySet = new Set((itineraryLinks.data || []).map((r: any) => r.blog_post_id));

  const enhancedData = (data || []).map(p => ({
    ...p,
    hasPackingList: packingSet.has(p.id),
    hasItineraries: itinerarySet.has(p.id),
  }));

  console.log('[DEBUG] All posts result:', { count, dataLength: enhancedData?.length });
  return { data: enhancedData || [], count: count || 0 };
}
